import cloneDeep from "lodash.clonedeep";
import { ActionTypes } from "store/actionTypes";
import IntegrationState from "models/state/integrationState";
import Action from "store/action";

const initialState: IntegrationState = {
    isLoading: false,
    error: null,
    
    // Customer data
    customers: [],
    customersLoading: false,
    customersLoaded: false,
    customersError: null,
    selectedCustomer: null,
    
    // User data
    users: [],
    // MSP data
    msps: [],
    selectedMsp: null,
    
    // Application data
    applications: [],
    applicationsLoading: false,
    applicationsLoaded: false,
    applicationsError: null,
    selectedApplication: null,
    applicationPermissions: null,
    
    // Action data
    applicationActions: [],
    applicationActionsLoading: false,
    applicationActionsLoaded: false,
    applicationActionsError: null,
    baselineActions: [],
    
    // Platform data
    platforms: [],
    platformsLoading: false,
    platformsLoaded: false,
    platformsError: null,
    platformActions: [],
    platformActionsLoading: false,
    platformActionsLoaded: false,
    platformActionsError: null,
    baselinePlatformActions: [],
    
    // Statistics and status
    applicationStats: null,
    platformIntegrationStatus: [],
    
    // Audit logs
    auditLogs: [],
    
    // Pagination and filters
    pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
    },
    
    filters: {
        customerId: undefined,
        applicationId: undefined,
        platformType: undefined,
        status: undefined,
    },

    // Load test results from localStorage if available
    testResults: (() => {
        try {
            const stored = localStorage.getItem('integration_test_results');
            if (stored) {
                const parsed = JSON.parse(stored);
                // Clean up old test results (older than 24 hours)
                const now = Date.now();
                const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
                const cleaned = Object.entries(parsed).reduce((acc, [key, value]) => {
                    const testResult = value as { timestamp?: number };
                    if (testResult.timestamp && (now - testResult.timestamp) < oneDay) {
                        acc[key] = value;
                    }
                    return acc;
                }, {} as Record<string, any>);
                
                // Save cleaned results back to localStorage
                localStorage.setItem('integration_test_results', JSON.stringify(cleaned));
                return cleaned;
            }
            return {};
        } catch (error) {
            console.warn('Failed to load test results from localStorage:', error);
            return {};
        }
    })(),
};

const integrationReducer = (state: IntegrationState = initialState, action: Action): IntegrationState => {
    let newState = cloneDeep(state);

    switch (action.type) {
        case ActionTypes.INTEGRATION_LOADING:
            newState.isLoading = true;
            newState.error = null;
            return newState;

        case ActionTypes.INTEGRATION_ERROR:
            newState.isLoading = false;
            const errorMessage = action.payload?.error?.message || 
                                action.payload?.message || 
                                action.payload?.error || 
                                action.payload || 
                                "An error occurred";
            newState.error = typeof errorMessage === 'string' ? errorMessage : 'An error occurred';
            return newState;

        // Customer Management
        case ActionTypes.GET_CUSTOMERS_REQUEST:
            newState.isLoading = true;
            newState.error = null;
            newState.customersLoading = true;
            newState.customersError = null;
            return newState;

        case ActionTypes.GET_CUSTOMERS:
            newState.isLoading = false;
            newState.customersLoading = false;
            newState.customersLoaded = true;
            newState.customersError = null;
            const customersData = action.payload?.rows || action.payload || [];
            newState.customers = customersData;
            return newState;

        case ActionTypes.GET_CUSTOMERS_FAILURE:
            newState.isLoading = false;
            newState.customersLoading = false;
            newState.customersLoaded = false;
            const customersErrorMessage = action.payload?.error?.message ||
                                          action.payload?.message ||
                                          action.payload?.error ||
                                          action.payload ||
                                          "Failed to fetch customers";
            newState.customersError = typeof customersErrorMessage === 'string' ? customersErrorMessage : 'Failed to fetch customers';
            newState.error = newState.customersError;
            return newState;

        case ActionTypes.GET_CUSTOMER_BY_ID:
            newState.isLoading = false;
            newState.selectedCustomer = action.payload;
            return newState;

        case ActionTypes.CREATE_CUSTOMER:
            newState.isLoading = false;
            if (action.payload) {
                newState.customers = [...newState.customers, action.payload];
            }
            return newState;

        case ActionTypes.UPDATE_CUSTOMER:
            newState.isLoading = false;
            if (action.payload) {
                newState.customers = newState.customers.map(customer =>
                    customer.id === action.payload.id ? action.payload : customer
                );
                if (newState.selectedCustomer?.id === action.payload.id) {
                    newState.selectedCustomer = action.payload;
                }
            }
            return newState;

        case ActionTypes.DELETE_CUSTOMER:
            newState.isLoading = false;
            if (action.payload) {
                newState.customers = newState.customers.filter(customer =>
                    customer.id !== action.payload
                );
                if (newState.selectedCustomer?.id === action.payload) {
                    newState.selectedCustomer = null;
                }
            }
            return newState;

        case ActionTypes.GET_CUSTOMERS_BY_MSP_ID:
            newState.isLoading = false;
            newState.customersLoading = false;
            newState.customersLoaded = true;
            newState.customersError = null;
            newState.customers = action.payload?.rows || action.payload || [];
            return newState;

        // User Management
        case ActionTypes.GET_USERS_BY_MSP_ID:
            newState.isLoading = false;
            newState.users = action.payload?.rows || action.payload || [];
            return newState;

        case ActionTypes.CREATE_USER:
            newState.isLoading = false;
            if (action.payload) {
                newState.users = [...newState.users, action.payload];
            }
            return newState;

        case ActionTypes.UPDATE_USER:
            newState.isLoading = false;
            if (action.payload) {
                newState.users = newState.users.map(user =>
                    user.id === action.payload.id ? action.payload : user
                );
            }
            return newState;

        case ActionTypes.DELETE_USER:
            newState.isLoading = false;
            if (action.payload !== undefined && action.payload !== null) {
                const deleteId = String(action.payload);
                newState.users = newState.users.filter(user => String(user.id) !== deleteId);
            }
            return newState;

        // MSP Management
        case ActionTypes.GET_MSPS:
            newState.isLoading = false;
            const mspsData = action.payload?.rows || action.payload || [];
            newState.msps = mspsData;
            return newState;

        case ActionTypes.GET_MSP_BY_ID:
            newState.isLoading = false;
            newState.selectedMsp = action.payload;
            return newState;

        // Application Management
        case ActionTypes.CLEAR_APPLICATIONS:
            newState.applications = [];
            newState.applicationActions = [];
            newState.baselineActions = [];
            return newState;

        case ActionTypes.GET_APPLICATIONS_BY_CUSTOMER_REQUEST:
            newState.isLoading = true;
            newState.error = null;
            newState.applicationsLoading = true;
            newState.applicationsLoaded = false;
            newState.applicationsError = null;
            return newState;

        case ActionTypes.GET_APPLICATIONS_BY_CUSTOMER:
            newState.isLoading = false;
            newState.applicationsLoading = false;
            newState.applicationsLoaded = true;
            newState.applicationsError = null;
            // Handle both direct array and { rows: [...] } response structures
            const applicationsData = action.payload?.rows || action.payload || [];
            newState.applications = applicationsData;
            return newState;

        case ActionTypes.GET_APPLICATIONS_BY_CUSTOMER_FAILURE: {
            newState.isLoading = false;
            newState.applicationsLoading = false;
            newState.applicationsLoaded = false;
            const applicationsErrorMessage = action.payload?.error?.message ||
                                             action.payload?.message ||
                                             action.payload?.error ||
                                             action.payload ||
                                             "Failed to fetch applications";
            newState.applicationsError =
                typeof applicationsErrorMessage === 'string' ? applicationsErrorMessage : 'Failed to fetch applications';
            newState.error = newState.applicationsError;
            return newState;
        }

        case ActionTypes.GET_APPLICATION_DETAILS:
            newState.isLoading = false;
            newState.selectedApplication = action.payload;
            return newState;

        case ActionTypes.GET_APPLICATION_PERMISSIONS:
            newState.isLoading = false;
            newState.applicationPermissions = action.payload;
            return newState;

        case ActionTypes.UPDATE_APPLICATION_STATE:
            newState.isLoading = false;
            if (action.payload && typeof action.payload === 'object' && 'id' in action.payload) {
                const updatedApp = action.payload;
                newState.applications = newState.applications.map(app =>
                    app.id.toString() === updatedApp.id.toString() ? { ...app, ...updatedApp } : app
                );
            }
            return newState;

        case ActionTypes.UPDATE_APPLICATION_CONFIG:
            newState.isLoading = false;
            if (action.payload && typeof action.payload === 'object' && 'id' in action.payload) {
                const updatedApp = action.payload;
                newState.applications = newState.applications.map(app =>
                    app.id.toString() === updatedApp.id.toString() ? { ...app, ...updatedApp } : app
                );
            }
            return newState;

        case ActionTypes.TEST_APPLICATION_CONFIGURATION:
            newState.isLoading = false;
            return newState;

        case ActionTypes.SET_TEST_RESULT:
            newState.isLoading = false;
            if (action.payload && action.payload.clearAll) {
                // Clear all test results
                newState.testResults = {};
                localStorage.removeItem('integration_test_results');
            } else if (action.payload && action.payload.clearForCustomer && action.payload.customerId) {
                // Clear test results for specific customer
                const customerId = action.payload.customerId;
                Object.keys(newState.testResults).forEach(key => {
                    if (key.startsWith(`${customerId}:`)) {
                        delete newState.testResults[key];
                    }
                });
                // Save updated results to localStorage
                localStorage.setItem('integration_test_results', JSON.stringify(newState.testResults));
            } else if (action.payload && typeof action.payload.clearCompositeKey === 'string') {
                const k = action.payload.clearCompositeKey;
                if (newState.testResults[k]) {
                    delete newState.testResults[k];
                    localStorage.setItem('integration_test_results', JSON.stringify(newState.testResults));
                }
            } else if (action.payload && action.payload.customerId && action.payload.applicationId) {
                // Store test result with composite key
                const { customerId, applicationId, result } = action.payload;
                const compositeKey = `${customerId}:${applicationId}`;
                newState.testResults[compositeKey] = {
                    ...result,
                    timestamp: Date.now()
                };
                // Save updated results to localStorage
                localStorage.setItem('integration_test_results', JSON.stringify(newState.testResults));
            }
            return newState;

        case ActionTypes.ADD_APPLICATION_TO_CUSTOMER:
            newState.isLoading = false;
            if (action.payload) {
                newState.applications = [...newState.applications, action.payload];
            }
            return newState;

        case ActionTypes.REMOVE_APPLICATION_FROM_CUSTOMER:
            newState.isLoading = false;
            if (action.payload && action.payload.id) {
                newState.applications = newState.applications.filter(app => app.id !== action.payload.id);
            }
            return newState;

        // Action Management
        case ActionTypes.GET_APPLICATION_ACTIONS:
            newState.isLoading = false;
            newState.applicationActionsLoading = false;
            newState.applicationActionsLoaded = true;
            newState.applicationActionsError = null;
            // Backend now returns { rows: [...], baseline: [...] }
            if (action.payload && typeof action.payload === 'object') {
                newState.applicationActions = action.payload.rows || action.payload || [];
                newState.baselineActions = action.payload.baseline || [];
            } else {
                // Fallback for old response format
                newState.applicationActions = action.payload?.rows || action.payload || [];
                newState.baselineActions = [];
            }
            return newState;

        case ActionTypes.CLEAR_APPLICATION_ACTIONS:
            newState.isLoading = false;
            newState.applicationActions = [];
            newState.baselineActions = [];
            newState.applicationActionsLoading = false;
            newState.applicationActionsLoaded = false;
            newState.applicationActionsError = null;
            return newState;

        case ActionTypes.GET_APPLICATION_ACTIONS_REQUEST:
            newState.isLoading = true;
            newState.error = null;
            newState.applicationActionsLoading = true;
            newState.applicationActionsLoaded = false;
            newState.applicationActionsError = null;
            return newState;

        case ActionTypes.GET_APPLICATION_ACTIONS_FAILURE: {
            newState.isLoading = false;
            newState.applicationActionsLoading = false;
            newState.applicationActionsLoaded = false;
            const applicationActionsErrorMessage = action.payload?.error?.message ||
                                                   action.payload?.message ||
                                                   action.payload?.error ||
                                                   action.payload ||
                                                   "Failed to fetch application actions";
            newState.applicationActionsError =
                typeof applicationActionsErrorMessage === 'string' ? applicationActionsErrorMessage : 'Failed to fetch application actions';
            newState.error = newState.applicationActionsError;
            return newState;
        }

        case ActionTypes.UPDATE_ACTION_PERMISSION:
            newState.isLoading = false;
            if (action.payload && action.payload.name) {
                // Update the action with the new permission and override status
                newState.applicationActions = newState.applicationActions.map(actionItem =>
                    actionItem.name === action.payload.name 
                        ? { 
                            ...actionItem, 
                            permission: action.payload.permission,
                            baseline_permission: action.payload.baseline_permission,
                            is_override: action.payload.is_override
                          }
                        : actionItem
                );
            }
            return newState;

        case ActionTypes.UPDATE_ACTION_CONFIG:
            newState.isLoading = false;
            if (action.payload && action.payload.name) {
                newState.applicationActions = newState.applicationActions.map(actionItem =>
                    actionItem.name === action.payload.name 
                        ? { ...actionItem, config: action.payload.config }
                        : actionItem
                );
            }
            return newState;

        case ActionTypes.UPDATE_PLATFORM_STATE: {
            const enabledPlatformId = String(action.payload?.id);
            const isPsaEnableAction =
                String(action.payload?.type || "").toLowerCase() === "psa" &&
                Boolean(action.payload?.enabled);
            return {
                ...state,
                platforms: state.platforms.map(platform =>
                    String(platform.id) === enabledPlatformId
                        ? { ...platform, enabled: action.payload.enabled }
                        : isPsaEnableAction && String(platform.type || "").toLowerCase() === "psa"
                        ? { ...platform, enabled: false }
                        : platform
                ),
                isLoading: false,
            };
        }

        case ActionTypes.UPDATE_PLATFORM_ACTION_PERMISSION:
            newState.isLoading = false;
            if (action.payload && (action.payload.name || action.payload.id)) {
                newState.platformActions = newState.platformActions.map(actionItem =>
                    (action.payload.name && actionItem.name === action.payload.name) ||
                    actionItem.id === action.payload.id
                        ? { 
                            ...actionItem, 
                            ...action.payload,
                            permission: action.payload.permission,
                            baseline_permission: action.payload.baseline_permission,
                            is_override: action.payload.is_override
                          }
                        : actionItem
                );
            }
            return newState;

        // Platform Management
        case ActionTypes.GET_PLATFORMS_REQUEST:
            newState.isLoading = true;
            newState.error = null;
            newState.platformsLoading = true;
            newState.platformsLoaded = false;
            newState.platformsError = null;
            return newState;

        case ActionTypes.GET_PLATFORMS:
            newState.isLoading = false;
            newState.platformsLoading = false;
            newState.platformsLoaded = true;
            newState.platformsError = null;
            newState.platforms = action.payload || [];
            return newState;

        case ActionTypes.GET_PLATFORMS_FAILURE: {
            newState.isLoading = false;
            newState.platformsLoading = false;
            newState.platformsLoaded = false;
            const platformsErrorMessage = action.payload?.error?.message ||
                                          action.payload?.message ||
                                          action.payload?.error ||
                                          action.payload ||
                                          "Failed to fetch platforms";
            newState.platformsError = typeof platformsErrorMessage === 'string' ? platformsErrorMessage : 'Failed to fetch platforms';
            newState.error = newState.platformsError;
            return newState;
        }

        case ActionTypes.GET_PLATFORM_ACTIONS:
            newState.isLoading = false;
            newState.platformActionsLoading = false;
            newState.platformActionsLoaded = true;
            newState.platformActionsError = null;
            // Handle both direct payload (array) and response object with rows/baseline
            if (Array.isArray(action.payload)) {
                newState.platformActions = action.payload;
                newState.baselinePlatformActions = [];
            } else if (action.payload && action.payload.rows) {
                newState.platformActions = action.payload.rows || [];
                newState.baselinePlatformActions = action.payload.baseline || [];
            } else {
                newState.platformActions = [];
                newState.baselinePlatformActions = [];
            }
            return newState;

        case ActionTypes.GET_PLATFORM_ACTIONS_REQUEST:
            newState.isLoading = true;
            newState.error = null;
            newState.platformActionsLoading = true;
            newState.platformActionsLoaded = false;
            newState.platformActionsError = null;
            return newState;

        case ActionTypes.GET_PLATFORM_ACTIONS_FAILURE: {
            newState.isLoading = false;
            newState.platformActionsLoading = false;
            newState.platformActionsLoaded = false;
            const platformActionsErrorMessage = action.payload?.error?.message ||
                                                action.payload?.message ||
                                                action.payload?.error ||
                                                action.payload ||
                                                "Failed to fetch platform actions";
            newState.platformActionsError =
                typeof platformActionsErrorMessage === 'string' ? platformActionsErrorMessage : 'Failed to fetch platform actions';
            newState.error = newState.platformActionsError;
            return newState;
        }

        case ActionTypes.GET_AVAILABLE_PLATFORMS:
        case ActionTypes.GET_AVAILABLE_APPLICATIONS:
            // These are used by AddIntegrationModal; ensure global loading always clears.
            newState.isLoading = false;
            return newState;

        // Statistics and Audit
        case ActionTypes.GET_APPLICATION_STATS:
            newState.isLoading = false;
            newState.applicationStats = action.payload;
            return newState;

        case ActionTypes.GET_PLATFORM_INTEGRATION_STATUS:
            newState.isLoading = false;
            newState.platformIntegrationStatus = action.payload || [];
            return newState;

        case ActionTypes.GET_ACTION_AUDIT_LOG:
            newState.isLoading = false;
            newState.auditLogs = action.payload || [];
            return newState;

        case ActionTypes.UPDATE_PLATFORM_CONFIG:
            newState.isLoading = false;
            if (action.payload && action.payload.platformId) {
                newState.platforms = newState.platforms.map(platform =>
                    platform.id.toString() === action.payload.platformId
                        ? { ...platform, config_json: action.payload.config }
                        : platform
                );
            }
            return newState;

        default:
            return newState;
    }
};

export default integrationReducer;
